/**
 * Authentication API Service
 *
 * Centralized service for all authentication-related API calls.
 */

import api from '@/lib/api'

export interface User {
  id: number
  name: string
  email: string
  email_verified_at: string | null
  workspaces: Workspace[]
  created_at: string
  updated_at: string
}

export interface Workspace {
  id: number
  uuid: string
  name: string
  slug: string
  credit_balance: number
  created_at: string
  updated_at: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  name: string
  email: string
  password: string
  password_confirmation: string
}



class AuthService {
  /**
   * Get the current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<{ data: User } | User>('/api/auth/user')
    // Handle both wrapped (JsonResource) and unwrapped responses
    const data = 'data' in response.data ? response.data.data : response.data
    return data
  }

  /**
   * Login with email and password (Session-based)
   */
  async login(credentials: LoginCredentials): Promise<User> {
    // Get CSRF cookie first (required by Sanctum for SPA auth)
    await api.get('/sanctum/csrf-cookie')

    // Login - session created automatically
    const response = await api.post<{ user: User }>('/api/auth/login', credentials)

    // Store user in localStorage for persistence (optional)
    localStorage.setItem('user', JSON.stringify(response.data.user))

    return response.data.user
  }

  /**
   * Register a new user (Session-based)
   */
  async register(data: RegisterData): Promise<User> {
    // Get CSRF cookie first
    await api.get('/sanctum/csrf-cookie')

    // Register - session created automatically
    const response = await api.post<{ user: User }>('/api/auth/register', data)

    // Store user in localStorage for persistence (optional)
    localStorage.setItem('user', JSON.stringify(response.data.user))

    return response.data.user
  }

  /**
   * Logout the current user (Session-based)
   */
  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout')
    } finally {
      // Clear local storage
      localStorage.removeItem('user')
      // Session destroyed server-side, cookie cleared automatically
    }
  }

  /**
   * Check if user is authenticated (check if we can get current user)
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser()
      return true
    } catch {
      return false
    }
  }

  /**
   * Get stored user from localStorage
   */
  getStoredUser(): User | null {
    const userJson = localStorage.getItem('user')
    return userJson ? JSON.parse(userJson) : null
  }
}

export const authService = new AuthService()
export default authService
